import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ThemePreference } from '@mg/shared';
import {
  applyTheme,
  readStoredPreference,
  resolveTheme,
  SYSTEM_DARK_QUERY,
  systemPrefersDark,
  writeStoredPreference,
  type ResolvedTheme,
} from './themeStorage';

interface ThemeContextValue {
  /** What the user chose (My Profile > Appearance). */
  preference: ThemePreference;
  /** What is on screen now. SYSTEM resolves through the OS setting, so this is what "Currently …" shows. */
  resolved: ResolvedTheme;
  /**
   * Apply a choice at once and keep this browser's copy. It does NOT save it for the user: the
   * Appearance card does that (this provider sits above AuthProvider and cannot reach the user).
   */
  setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * A layout effect in the browser, so <html> flips in the same frame as the render that changed the
 * theme. A plain effect where there is no DOM (a static render in a test), where React would otherwise
 * warn that a layout effect does nothing on the server; it runs no effect there either way.
 */
const useBrowserLayoutEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect;

/** The parts of a MediaQueryList used here. Safari before 14 has only addListener / removeListener. */
interface SchemeQuery {
  readonly matches: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
}

function openSchemeQuery(): SchemeQuery | null {
  try {
    return typeof window.matchMedia === 'function' ? window.matchMedia(SYSTEM_DARK_QUERY) : null;
  } catch {
    return null;
  }
}

/**
 * The app's theme, for every tree (the agency app, sign-in, the approval portal, the guides): mounted
 * outermost in App.tsx. It starts from this browser's copy, the same input the inline script in
 * index.html painted the first frame with, so the first render agrees with what is already on screen.
 * The signed-in user's saved choice arrives later through ThemeServerSync (agency app only).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [osDark, setOsDark] = useState<boolean>(systemPrefersDark);

  // System follows the OS live: switching the OS to dark while the app is open turns it dark.
  useEffect(() => {
    const query = openSchemeQuery();
    if (!query) return;
    const onChange = () => setOsDark(query.matches);
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener?.(onChange);
    // Read it again now that we are listening: the OS may have switched since the first render.
    onChange();
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else query.removeListener?.(onChange);
    };
  }, []);

  const resolved = resolveTheme(preference, osDark);
  useBrowserLayoutEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writeStoredPreference(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
